import { TYPE_BEAT_ENUM, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { defaultControlModalValue } from '@/entities/report';
import useMenuHrChart from './useMenuHrChart';

export type MenuHrChartProps = {
  selectedEvent?: any;
  hidePopover: () => void;
};

/**
 * Renders the context menu shown for an event picked on the HR chart, offering delete, mark-as-artifact, duration and type actions. Reads isSelectNewEvent, the modal-control setter and onClickButton from useMenuHrChart, then renders whichever button set generateMenu selects for the event type.
 *
 * @param props - The selected event and the hidePopover callback closing the surrounding popover.
 * @returns The menu buttons applicable to the selected event.
 */
const MenuHrChart = ({ selectedEvent, hidePopover }: MenuHrChartProps) => {
  const { isSelectNewEvent, setControlModal, onClickButton } = useMenuHrChart({ selectedEvent });

  /**
   * Renders the Change event duration entry of the HR chart context menu. On click it hides the popover and resets the modal control state with isShowChangeDurationSelectedInfo enabled, and it stays disabled while a new event is being selected.
   *
   * @returns The Change event duration menu button.
   */
  const buttonChangeDuration = () => (
    <button
      className="item-more"
      type="button"
      disabled={isSelectNewEvent}
      onClick={() => {
        hidePopover();
        setControlModal({ ...defaultControlModalValue, isShowChangeDurationSelectedInfo: true });
      }}
    >
      Change event duration
    </button>
  );

  /**
   * Renders the Change event type entry of the HR chart context menu. On click it hides the popover and resets the modal control state with isShowChangeTypeSingle enabled, and it stays disabled while a new event is being selected.
   *
   * @returns The Change event type menu button.
   */
  const buttonChangeType = () => (
    <button
      className="item-more"
      type="button"
      disabled={isSelectNewEvent}
      onClick={() => {
        hidePopover();
        setControlModal({ ...defaultControlModalValue, isShowChangeTypeSingle: true });
      }}
    >
      Change event type
    </button>
  );

  /**
   * Renders the Delete event entry of the HR chart context menu. On click it hides the popover and calls onClickButton with the deleted beat type, and it stays disabled while a new event is being selected.
   *
   * @returns The Delete event menu button.
   */
  const buttonDelete = () => (
    <button
      className="item-more"
      type="button"
      disabled={isSelectNewEvent}
      onClick={() => {
        hidePopover();
        onClickButton(TYPE_BEAT_ENUM.DELETED);
      }}
    >
      Delete event
    </button>
  );

  /**
   * Renders the Mark as artifact entry of the HR chart context menu. On click it hides the popover and calls onClickButton with the artifact rhythm type, and it stays disabled while a new event is being selected.
   *
   * @returns The Mark as artifact menu button.
   */
  const buttonMarkArtifact = () => (
    <button
      className="item-more"
      type="button"
      disabled={isSelectNewEvent}
      onClick={() => {
        hidePopover();
        onClickButton(TYPE_RHYTHM_EVENT_ENUM.ARTIFACT);
      }}
    >
      Mark as artifact
    </button>
  );

  /**
   * Chooses which HR chart context-menu buttons apply to the event currently selected. Switches on the event type so AFib offers delete, mark-as-artifact and change-duration, an artifact event offers only delete and change-duration, and every other type additionally offers change event type.
   *
   * @returns A fragment holding the menu buttons for the selected event type.
   */
  const generateMenu = () => {
    switch (selectedEvent.type) {
      case TYPE_RHYTHM_EVENT_ENUM.AFIB: {
        return (
          <>
            {buttonDelete()}
            {buttonMarkArtifact()}
            {buttonChangeDuration()}
          </>
        );
      }
      case TYPE_RHYTHM_EVENT_ENUM.ARTIFACT: {
        return (
          <>
            {buttonDelete()}
            {buttonChangeDuration()}
          </>
        );
      }
      default: {
        return (
          <>
            {buttonDelete()}
            {buttonMarkArtifact()}
            {buttonChangeDuration()}
            {buttonChangeType()}
          </>
        );
      }
    }
  };

  return <>{generateMenu()}</>;
};

export default MenuHrChart;
